/**
 * GymLogoUpload Component
 *
 * Allows gym owners to upload and manage their gym's logo.
 * Supports image preview, upload to Firebase Storage, and removal.
 * Uses ImageDropZone for consistent drag-and-drop file selection.
 */

import { useState, type CSSProperties } from 'react';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFirebaseStorage } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { ImageDropZone, MIME_TO_EXTENSION } from '@/components/common/ImageDropZone';

/**
 * Maximum logo file size (5MB)
 */
const MAX_LOGO_SIZE = 5 * 1024 * 1024;

/**
 * Allowed MIME types for logos
 */
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Props for GymLogoUpload component
 */
interface GymLogoUploadProps {
  /** Current logo URL (if any) */
  currentLogoUrl: string | null;
  /** Callback when logo changes */
  onLogoChange: (logoUrl: string | null) => Promise<void>;
  /** Gym ID for storage path */
  gymId: string;
}

/**
 * GymLogoUpload allows uploading and managing gym logos
 */
export const GymLogoUpload = ({ currentLogoUrl, onLogoChange, gymId }: GymLogoUploadProps) => {
  const { user } = useAuth();

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [removing, setRemoving] = useState(false);

  /**
   * Handle file selection from ImageDropZone - auto-uploads immediately
   */
  const handleFileSelect = (file: File) => {
    uploadFile(file);
  };

  /**
   * Handle validation error from ImageDropZone
   */
  const handleDropZoneError = (message: string) => {
    setError(message);
  };

  /**
   * Upload a file immediately (auto-upload behavior)
   */
  const uploadFile = async (file: File) => {
    if (!user) return;

    setError(null);
    setSuccess(false);
    setUploading(true);
    setProgress(0);

    // Create preview URL for display during upload
    const preview = URL.createObjectURL(file);
    setPreviewUrl(preview);

    try {
      const storage = getFirebaseStorage();
      const extension = MIME_TO_EXTENSION[file.type] ?? 'jpg';
      const timestamp = Date.now();
      const storagePath = `gyms/${gymId}/logo_${timestamp}.${extension}`;
      const storageRef = ref(storage, storagePath);

      // Upload with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          uploadedBy: user.uid,
          originalName: file.name,
        },
      });

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setProgress(pct);
          },
          (uploadError) => {
            reject(uploadError);
          },
          async () => {
            try {
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

              // Update gym with new logo URL
              await onLogoChange(downloadUrl);

              // Clean up preview
              URL.revokeObjectURL(preview);
              setPreviewUrl(null);
              setSuccess(true);

              resolve();
            } catch (err) {
              reject(err);
            }
          }
        );
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload logo';
      setError(errorMessage);
      console.error('Logo upload error:', err);
      // Clean up preview on error
      URL.revokeObjectURL(preview);
      setPreviewUrl(null);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  /**
   * Remove the current logo
   */
  const handleRemoveLogo = async () => {
    if (!currentLogoUrl) return;

    setRemoving(true);
    setError(null);
    setSuccess(false);

    try {
      // Try to delete from storage (may fail if URL format is different)
      try {
        const storage = getFirebaseStorage();
        const storageRef = ref(storage, currentLogoUrl);
        await deleteObject(storageRef);
      } catch (deleteErr) {
        // Ignore deletion errors - URL may be from different source
        console.warn('Could not delete logo from storage:', deleteErr);
      }

      // Update gym to remove logo
      await onLogoChange(null);
      setSuccess(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove logo';
      setError(errorMessage);
      console.error('Logo removal error:', err);
    } finally {
      setRemoving(false);
    }
  };

  // Determine what to show
  const displayUrl = previewUrl ?? currentLogoUrl;

  return (
    <div style={containerStyle}>
      {/* Logo Display Area */}
      <div style={logoAreaContainerStyle}>
        {!displayUrl ? (
          // No logo - show drop zone
          <ImageDropZone
            onFileSelect={handleFileSelect}
            onError={handleDropZoneError}
            maxSize={MAX_LOGO_SIZE}
            allowedTypes={ALLOWED_MIME_TYPES}
            placeholder="Drag & drop logo or click to browse"
            previewHeight={200}
            disabled={uploading || removing}
          />
        ) : (
          // Has logo - show image with upload progress if uploading
          <div style={logoAreaStyle}>
            <img src={displayUrl} alt="Gym logo" style={logoImageStyle} />

            {/* Upload Progress Overlay */}
            {uploading && (
              <div style={progressOverlayStyle}>
                <div style={progressBarContainerStyle}>
                  <div
                    style={{
                      ...progressBarStyle,
                      width: `${progress}%`,
                    }}
                  />
                </div>
                <span style={progressTextStyle}>{progress}%</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons - only show when not uploading and have existing logo */}
      {currentLogoUrl && !uploading && (
        <div style={actionsStyle}>
          <ImageDropZone
            onFileSelect={handleFileSelect}
            onError={handleDropZoneError}
            maxSize={MAX_LOGO_SIZE}
            allowedTypes={ALLOWED_MIME_TYPES}
            placeholder="Change Logo"
            disabled={uploading || removing}
          />
          <button
            type="button"
            onClick={handleRemoveLogo}
            disabled={uploading || removing}
            style={{
              ...removeButtonStyle,
              ...(uploading || removing ? disabledButtonStyle : {}),
            }}
          >
            {removing ? 'Removing...' : 'Remove Logo'}
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && <div style={errorStyle}>{error}</div>}

      {/* Success Message */}
      {success && <div style={successStyle}>Logo updated successfully!</div>}

      {/* Help Text */}
      <p style={helpTextStyle}>
        JPEG, PNG, WebP, or GIF. Maximum 5MB. Recommended size: 200x200 pixels.
      </p>
    </div>
  );
};

/**
 * Component styles using CSS variables for dark mode support
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const logoAreaContainerStyle: CSSProperties = {
  maxWidth: '200px',
};

const logoAreaStyle: CSSProperties = {
  position: 'relative',
  width: '200px',
  height: '200px',
  borderRadius: '12px',
  overflow: 'hidden',
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-bg-secondary)',
};

const logoImageStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const progressOverlayStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
};

const progressBarContainerStyle: CSSProperties = {
  width: '80%',
  height: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  borderRadius: '4px',
  overflow: 'hidden',
};

const progressBarStyle: CSSProperties = {
  height: '100%',
  backgroundColor: 'var(--color-primary)',
  transition: 'width 0.2s',
};

const progressTextStyle: CSSProperties = {
  color: 'white',
  fontSize: '1rem',
  fontWeight: 600,
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
};

const removeButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  color: 'var(--color-error)',
  border: '1px solid var(--color-error)',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const disabledButtonStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const errorStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  border: '1px solid var(--color-error)',
  color: 'var(--color-error)',
  fontSize: '0.875rem',
};

const successStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-success-bg)',
  border: '1px solid var(--color-success)',
  color: 'var(--color-badge-success-text)',
  fontSize: '0.875rem',
};

const helpTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
  margin: 0,
};

export default GymLogoUpload;
